import { Component, EventEmitter, Input, Output, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Subject, takeUntil } from 'rxjs';
import { UserService } from 'app/core/user/user.service';
import { UserService as AccessControlUserService } from 'app/modules/administration/access-control/user-management/user.service';
import { LoadingSpinnerComponent } from 'app/modules/platform/mini-dashboard/components/loading-spinner/loading-spinner.component';

@Component({
  selector: 'app-change-password-drawer',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    LoadingSpinnerComponent,
  ],
  template: `
    <!-- Panel Overlay -->
    <div
      *ngIf="isOpen"
      class="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 print:hidden"
      (click)="closeDrawer()"
    ></div>

    <!-- Right Side Drawer -->
    <div
      class="fixed top-0 right-0 h-full w-[500px] max-w-full bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out print:hidden"
      [class.translate-x-0]="isOpen"
      [class.translate-x-full]="!isOpen"
    >
      <!-- Panel Header - Matching Add Family Member blue theme -->
      <div
        class="flex items-center justify-between pb-2 border-b border-gray-200 bg-blue-500 mb-4 p-6"
      >
        <h2 class="text-2xl text-white font-semibold">Change Password</h2>
        <button mat-icon-button (click)="closeDrawer()">
          <mat-icon class="text-white">close</mat-icon>
        </button>
      </div>

      <!-- Panel Content -->
      <div class="space-y-4 pt-2 p-6">
        <form [formGroup]="changePasswordForm" (ngSubmit)="onSubmit()">
          <!-- Current Password -->
          <mat-form-field appearance="outline" class="w-full mb-4">
            <mat-label>Current Password</mat-label>
            <input
              matInput
              [type]="showCurrentPassword ? 'text' : 'password'"
              formControlName="currentPassword"
              placeholder="Enter your current password"
            />
            <button
              matSuffix
              mat-icon-button
              type="button"
              (click)="toggleCurrentPasswordVisibility()"
              class="password-toggle-btn"
            >
              <mat-icon>{{ showCurrentPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
            <mat-error *ngIf="changePasswordForm.get('currentPassword')?.hasError('required')">
              Current password is required
            </mat-error>
          </mat-form-field>

          <!-- New Password -->
          <mat-form-field appearance="outline" class="w-full mb-4">
            <mat-label>New Password</mat-label>
            <input
              matInput
              [type]="showNewPassword ? 'text' : 'password'"
              formControlName="newPassword"
              placeholder="Enter your new password"
            />
            <button
              matSuffix
              mat-icon-button
              type="button"
              (click)="toggleNewPasswordVisibility()"
              class="password-toggle-btn"
            >
              <mat-icon>{{ showNewPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
            <mat-error *ngIf="changePasswordForm.get('newPassword')?.hasError('required')">
              New password is required
            </mat-error>
            <mat-error *ngIf="changePasswordForm.get('newPassword')?.hasError('minlength')">
              Password must be at least 6 characters long
            </mat-error>
          </mat-form-field>

          <!-- Confirm Password -->
          <mat-form-field appearance="outline" class="w-full mb-6">
            <mat-label>Confirm Password</mat-label>
            <input
              matInput
              [type]="showConfirmPassword ? 'text' : 'password'"
              formControlName="confirmPassword"
              placeholder="Confirm your new password"
              [class.password-match]="
                passwordsMatch && changePasswordForm.get('confirmPassword')?.value
              "
              [class.password-mismatch]="hasPasswordMismatchError"
            />

            <!-- Password Toggle Button -->
            <button
              matSuffix
              mat-icon-button
              type="button"
              (click)="toggleConfirmPasswordVisibility()"
              class="password-toggle-btn mr-2"
              *ngIf="!passwordsMatch && !hasPasswordMismatchError"
            >
              <mat-icon>{{ showConfirmPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>

            <!-- Success Icon (Green Check) -->
            <mat-icon
              matSuffix
              *ngIf="passwordsMatch && changePasswordForm.get('confirmPassword')?.value"
              class="text-green-500 mr-2"
            >
              check_circle
            </mat-icon>

            <!-- Error Icon (Red X) -->
            <mat-icon matSuffix *ngIf="hasPasswordMismatchError" class="text-red-500 mr-2">
              error
            </mat-icon>

            <!-- Validation Messages -->
            <mat-error *ngIf="changePasswordForm.get('confirmPassword')?.hasError('required')">
              Please confirm your password
            </mat-error>
            <mat-error
              *ngIf="changePasswordForm.get('confirmPassword')?.hasError('passwordMismatch')"
            >
              ❌ New password and confirm password do not match
            </mat-error>

            <!-- Success Hint -->
            <mat-hint
              *ngIf="passwordsMatch && changePasswordForm.get('confirmPassword')?.value"
              class="text-green-600"
            >
              ✅ Passwords match perfectly!
            </mat-hint>

            <!-- Mismatch Warning Hint -->
            <mat-hint *ngIf="hasPasswordMismatchError" class="text-red-600">
              ❌ Passwords do not match - please check both fields
            </mat-hint>
          </mat-form-field>

          <!-- Submit Button - Matching family member create layout -->
          <div class="flex justify-center items-center gap-2 pt-4">
            <button mat-stroked-button type="button" (click)="closeDrawer()">Cancel</button>
            <button
              mat-flat-button
              color="primary"
              type="submit"
              [disabled]="changePasswordForm.invalid || isLoading || !passwordsMatch"
            >
              <app-loading-spinner
                *ngIf="isLoading"
                [inline]="true"
                [diameter]="20"
                style="display: inline-block; margin-right: 8px"
              ></app-loading-spinner>
              {{ isLoading ? 'Updating...' : 'Submit' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  `,
  styleUrls: ['./change-password-drawer.component.scss'],
})
export class ChangePasswordDrawerComponent implements OnInit, OnDestroy {
  @Input() isOpen = false;
  @Output() drawerClosed = new EventEmitter<void>();

  changePasswordForm: FormGroup;
  isLoading = false;

  // Password visibility toggles
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    private _formBuilder: FormBuilder,
    private _snackBar: MatSnackBar,
    private _userService: UserService,
    private _accessControlUserService: AccessControlUserService,
  ) {
    this.changePasswordForm = this._formBuilder.group(
      {
        currentPassword: ['', [Validators.required]],
        newPassword: ['', [Validators.required, Validators.minLength(6)]],
        confirmPassword: ['', [Validators.required]],
      },
      { validators: [this.passwordMatchValidator] },
    );
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  /**
   * Check if passwords match for UI feedback
   */
  get passwordsMatch(): boolean {
    const newPassword = this.changePasswordForm.get('newPassword')?.value;
    const confirmPassword = this.changePasswordForm.get('confirmPassword')?.value;
    return newPassword && confirmPassword && newPassword === confirmPassword;
  }

  /**
   * Check if confirm password field has mismatch error
   */
  get hasPasswordMismatchError(): boolean {
    const confirmPasswordControl = this.changePasswordForm.get('confirmPassword');
    return confirmPasswordControl?.hasError('passwordMismatch') && confirmPasswordControl?.touched;
  }

  /**
   * Toggle current password visibility
   */
  toggleCurrentPasswordVisibility(): void {
    this.showCurrentPassword = !this.showCurrentPassword;
  }

  /**
   * Toggle new password visibility
   */
  toggleNewPasswordVisibility(): void {
    this.showNewPassword = !this.showNewPassword;
  }

  /**
   * Toggle confirm password visibility
   */
  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  /**
   * Custom validator to check if new password and confirm password match
   */
  passwordMatchValidator(form: FormGroup) {
    const newPassword = form.get('newPassword');
    const confirmPassword = form.get('confirmPassword');

    // Only validate if both fields have values
    if (newPassword && confirmPassword && newPassword.value && confirmPassword.value) {
      if (newPassword.value !== confirmPassword.value) {
        // Set error on confirmPassword field specifically
        confirmPassword.setErrors({ passwordMismatch: true });
        return { passwordMismatch: true };
      } else {
        // Clear the passwordMismatch error if passwords match
        const errors = confirmPassword.errors;
        if (errors) {
          delete errors['passwordMismatch'];
          confirmPassword.setErrors(Object.keys(errors).length === 0 ? null : errors);
        }
      }
    }
    return null;
  }

  /**
   * Close the drawer
   */
  closeDrawer(): void {
    this.isOpen = false;
    this.changePasswordForm.reset();
    this.drawerClosed.emit();
  }

  /**
   * Handle form submission
   */
  onSubmit(): void {
    if (this.changePasswordForm.valid) {
      this.isLoading = true;
      const formValues = this.changePasswordForm.value;

      // Get current user ID
      this._userService.user$.pipe(takeUntil(this._unsubscribeAll)).subscribe((user) => {
        if (user?.id) {
          this._accessControlUserService
            .changePassword(user.id, {
              currentPassword: formValues.currentPassword,
              newPassword: formValues.newPassword,
            })
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
              next: (response) => {
                this.isLoading = false;
                this._snackBar.open(response.message || 'Password updated successfully!', 'Close', {
                  duration: 3000,
                  panelClass: ['snackbar-success'],
                });
                this.closeDrawer();
              },
              error: (error) => {
                this.isLoading = false;
                const errorMessage = error.error?.message || 'Failed to update password';
                this._snackBar.open(errorMessage, 'Close', {
                  duration: 3000,
                  panelClass: ['snackbar-error'],
                });
              },
            });
        } else {
          this.isLoading = false;
          this._snackBar.open('User not found', 'Close', { duration: 3000 });
        }
      });
    }
  }
}
